import { ref, computed } from 'vue';
import moment from 'moment';
import {
  PRODUCT_TYPES_WITH_OBJECT_TYPES,
  SUB_OBJECT_TYPES_WITH_K8S,
  GLOBAL_CDN
} from '@/containers/utils/constant';
import { getDefaultMetricPayload } from '@/containers/components/chart/useChart';
import { restManager } from '@/plugin/restManager';
import { scpmUtil } from '@/utils/scpmUtil';
import store from '@/store';
import { constant } from '@/utils/constant';
import {
  convertRawDataToDataChart,
  checkIsCollectionValue,
  getParamChart,
  getProductSqs
} from '@/containers/components/chart/utils';
import { useChart } from '@/composables/useChart.js';

export default function useInsightChart({
  selectedMetric,
  selectedTargets,
  selectedSubProduct,
  start,
  end,
  selectedProductType,
  collInterval
}) {
  const isShowChart = ref(false);
  const metricData = ref({
    eventName: '',
    eventKey: '',
    interval: '1m',
    statisticsTypeList: [],
    data: null,
    selectedIndividualItems: []
  });
  const { handleDataChart } = useChart();
  const individualItems = ref([]);
  const popupStyle = ref({});
  const paramDownloadChart = ref({});
  const isTitleClickable = ref(false);
  const startDate = computed(() => start.value ?? getDefaultTimeRange().startDate);
  const endDate = computed(() => end.value ?? getDefaultTimeRange().endDate);
  const isKubernetes = computed(() => selectedProductType.value.toLowerCase() === 'kubernetes');
  const isK8sObjectType = computed(
    () => isKubernetes.value && SUB_OBJECT_TYPES_WITH_K8S.includes(selectedSubProduct.value)
  );
  const hasSubType = computed(() =>
    PRODUCT_TYPES_WITH_OBJECT_TYPES.includes(selectedProductType.value)
  );
  const isK8s = computed(
    () => isK8sObjectType.value && !disabledIndividualItem(selectedMetric.value)
  );

  const getDefaultTimeRange = () => {
    const now = moment();
    const startTimeQuery =
      selectedProductType.value === GLOBAL_CDN
        ? moment(now).subtract(7, 'days')
        : moment(now).subtract(30, 'minutes');

    return {
      startDate: startTimeQuery.toISOString(),
      endDate: now.toISOString()
    };
  };

  const _getObjects = (metric) => {
    if (!selectedTargets.value.length) return false;

    const productIds = getProductSqs(selectedTargets.value);

    if (!productIds.length) return false;

    const params = {
      metricKeyList: [metric.metricKey],
      productIdList: getProductSqs(selectedTargets.value),
      queryStartDt: startDate.value,
      queryEndDt: endDate.value
    };

    return restManager.product
      .getProductObjects(params, selectedProductType.value, true)
      .then((data) => {
        individualItems.value = data.map((item) => ({
          ...item,
          value: item.objectName,
          key: item.objectName,
          text: item.objectDisplayName,
          isSelected: true
        }));
        return true;
      })
      .catch((e) => {
        if (e && e.code === 'AccessDenied') {
          scpmUtil.alertModal(e);
        }
        return false;
      });
  };

  const _getK8sObject = async () => {
    if (!selectedTargets.value.length) return false;
    const subTypeCode = selectedSubProduct.value;

    const [productIdList, productSubName, listDataCheckAuth] = [[], [], []];

    selectedTargets.value.forEach((target) => {
      productIdList.push(target.productSq);
      productSubName.push(target.objectName || target.productSubName);
      listDataCheckAuth.push({
        productResourceId: target.productSq,
        productName: target.productName
      });
    });

    const params = {
      productIdList,
      productObjectType: subTypeCode,
      productSubName,
      queryStartDt: startDate.value,
      queryEndDt: endDate.value
    };

    const payload = {
      productTypeCode: selectedProductType.value,
      params,
      listDataCheckAuth
    };

    const response = await store.dispatch('authStore/checkAuthorizationCluster', payload);

    if (response.type === constant.AuthorizationResult.ALLOW) {
      const data = response.responseData;
      individualItems.value = data.map((item) => ({
        ...item,
        value: item.objectName,
        key: item.objectName,
        text: item.objectDisplayName,
        isSelected: true
      }));
      return true;
    }

    return false;
  };

  const prepareChartData = async (firstFetch) => {
    if (selectedMetric.value.isObjectExist === 'Y' && !hasSubType.value) {
      await _getObjects(selectedMetric.value);
    }

    if (isK8sObjectType.value && !disabledIndividualItem(selectedMetric.value)) {
      await _getK8sObject();
    }

    getInitDataInsightChart(selectedMetric.value, firstFetch);
  };

  const toggleChart = async () => {
    if (!selectedTargets.value.length || !selectedMetric.value) return;

    if (!isShowChart.value) {
      await prepareChartData(true);
    }

    isShowChart.value = !isShowChart.value;
  };

  const hideChart = () => {
    isShowChart.value = false;
  };

  const getSelectedIndividualInsightChart = () => {
    return metricData.value?.selectedIndividualItems || [];
  };

  const disabledIndividualItem = (item) => {
    if (!item.isObjectExist || item.isObjectExist === 'N') return true;

    return item.isObjectExist === 'Y' && item.disableObject === 'Y';
  };

  const getDataInsightChart = async (
    metric,
    {
      selectedIndividualItems = [],
      interval,
      statisticsTypeList,
      eventName = '',
      eventKey = '',
      startTime,
      endTime
    }
  ) => {
    const hasSelectedTarget = selectedTargets.value.length;
    const hasIndividual = individualItems.value.length;
    const selectedIndividualEmpty = hasIndividual && !selectedIndividualItems.length;
    const isEqualTime = moment(startTime).valueOf() === moment(endTime).valueOf();

    if (!hasSelectedTarget || selectedIndividualEmpty || isEqualTime) {
      metricData.value = {
        data: [],
        selectedIndividualItems,
        statisticsTypeList,
        interval,
        eventName,
        eventKey
      };
      return;
    }

    const params = getParamChart({
      metric,
      isDownload: false,
      k8s: isK8s.value,
      hasSubType: !!selectedSubProduct.value,
      objectType: individualItems.value[0]?.objectType || 'pod',
      subTypeCode: selectedSubProduct.value,
      selectedTargets: selectedTargets.value,
      listIndividuals: individualItems.value.map((indiv) => indiv.key) || [],
      startTime,
      endTime,
      statisticsTypeList,
      selectedIndividualItems
    });

    return restManager.product
      .getProductMetricData({ ...params, interval }, selectedProductType.value, true, true)
      .then((res) => {
        // handle data of chart
        if (res.length) isTitleClickable.value = true;
        const rawData = res.map((item) => {
          item.perfData = handleDataChart(item.perfData, interval, startTime, endTime, false);
          return item;
        });
        const isCollectionValue = checkIsCollectionValue(
          interval,
          collInterval.value,
          selectedProductType.value
        );
        const hasIndividual = individualItems.value.length;
        metricData.value = {
          data: convertRawDataToDataChart({
            rawData,
            isCollectionValue,
            hasIndividual
          }),
          selectedIndividualItems,
          statisticsTypeList,
          interval,
          eventName,
          eventKey
        };
        return true;
      })
      .catch((e) => {
        if (e && e.code === 'AccessDenied') {
          scpmUtil.alertModal(e);
        }

        return false;
      });
  };

  const selectIndividualItems = (firstFetch = false) => {
    const beforeSelectedIndivList = getSelectedIndividualInsightChart();
    const beforeSelectedIndiv = beforeSelectedIndivList.filter((value) =>
      individualItems.value.some((item) => item.value === value)
    );
    if (firstFetch) {
      individualItems.value = individualItems.value.map((item) => ({
        ...item,
        isSelected: true
      }));
      return individualItems.value.map(({ value }) => value);
    }

    individualItems.value = individualItems.value.map((item) => ({
      ...item,
      isSelected: beforeSelectedIndiv.includes(item.value)
    }));
    return beforeSelectedIndiv;
  };

  const getInitDataInsightChart = (metric, firstFetch) => {
    const startTime = startDate.value;
    const endTime = endDate.value;
    const { statisticsTypeList, interval } = getDefaultMetricPayload(
      selectedProductType.value,
      startTime,
      endTime,
      collInterval.value
    );
    let selectedIndividualItems = selectIndividualItems(firstFetch);

    paramDownloadChart.value = {
      countSelectedObject: selectedIndividualItems.length,
      countSelectedTargets: selectedTargets.length,
      productTypeCode: selectedProductType.value,
      params: getParamChart({
        metric,
        isDownload: true,
        k8s: isK8s.value,
        hasSubType: !!selectedSubProduct.value,
        objectType: individualItems.value[0]?.objectType || 'pod',
        subTypeCode: selectedSubProduct.value,
        selectedTargets: selectedTargets.value,
        listIndividuals: individualItems.value.map((indiv) => indiv.key) || [],
        startTime,
        endTime
      })
    };

    return getDataInsightChart(metric, {
      selectedIndividualItems,
      statisticsTypeList,
      interval,
      startTime,
      endTime
    });
  };

  const handleChartChange = async (event) => {
    const {
      eventKey,
      eventName,
      query: { startTime, endTime, individualItems, interval, statisticalType }
    } = event;

    await getDataInsightChart(selectedMetric.value, {
      selectedIndividualItems: [...individualItems],
      statisticsTypeList: statisticalType,
      interval,
      eventName,
      eventKey,
      startTime: moment(startTime),
      endTime: moment(endTime)
    });
  };

  return {
    isShowChart,
    metricData,
    individualItems,
    popupStyle,
    startDate,
    endDate,
    paramDownloadChart,
    toggleChart,
    hideChart,
    handleChartChange,
    prepareChartData,
    isTitleClickable
  };
}
